import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Film, Headphones, ListChecks, ListPlus, Loader2, Lock, Pause, Play, Podcast, Share2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AppLink } from '@/components/ui/app-link'
import { ProgressBar, formatClock } from './ProgressBar'
import { paletteStyle, useCoverPalette } from './coverColor'
import { PlayerToneContext } from './tone'
import type { PlayerChapter } from './types'

export interface EpisodeEmbedCardProps {
  /** `compact`: the Feed's card; `full`: the episode page header (and the public embed's large size). */
  size?: 'compact' | 'full'
  title: string
  showTitle?: string | null
  /** The episode's page (the title's link). */
  href?: string | null
  /** The show's page (the show name's link). */
  showHref?: string | null
  coverUrl?: string | null
  /** What the cover shows when there is none. */
  kind?: 'audio' | 'video'
  duration: number
  /** The playhead, when this card's episode is the one loaded. */
  position?: number
  chapters?: PlayerChapter[]
  /** This card's episode is loaded in the player (the time reads "position / duration"). */
  current?: boolean
  playing?: boolean
  loading?: boolean
  disabled?: boolean
  onPlay?: () => void
  onSeek?: (seconds: number) => void
  /** Locked for the reader: no play button; the offer goes below the card. */
  locked?: React.ReactNode
  /** `inline`: the offer takes the time's row inside the card (the compact public embed, which has a fixed height). */
  lockedPlacement?: 'below' | 'inline'
  /** "Adicionar à fila" (only for someone signed in). */
  queue?: { inQueue: boolean; onToggle: () => void } | null
  onShare?: () => void
  /** The small mark on the corner (the public embed passes the platform's). */
  mark?: React.ReactNode
  /** The public embed (another site): `href` and `showHref` are full addresses opened in a new tab. */
  externalLinks?: boolean
  className?: string
  'data-testid'?: string
  'data-access'?: string
}

const ROUND = 'inline-flex shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]'

/**
 * The episode as Spotify's embed shows it: a rounded card filled with the
 * cover's dominant color, the cover on the left, the show and the title,
 * a big round play button, a slim bar with the time, and "Adicionar à fila"
 * and share as small icons. It reads no player state of its own, so the Feed,
 * the episode page and the public embed all build on it.
 */
export const EpisodeEmbedCard = React.forwardRef<HTMLDivElement, EpisodeEmbedCardProps>(({
  size = 'compact', title, showTitle, href, showHref, coverUrl, kind = 'audio', duration, position = 0, chapters,
  current = false, playing = false, loading = false, disabled = false, onPlay, onSeek, locked, lockedPlacement = 'below', queue, onShare, mark, externalLinks = false, className, ...data
}, ref) => {
  const { t } = useTranslation('player')
  const palette = useCoverPalette(coverUrl)
  const full = size === 'full'
  const FallbackIcon = kind === 'video' ? Film : Headphones
  const timeText = current && duration ? `${formatClock(position)} / ${formatClock(duration)}` : duration ? formatClock(duration) : ''
  const playLabel = playing ? t('pause') : t('playEpisode', { title })

  /* In the public embed there is no app around: the links leave the iframe for the community. */
  const link = (to: string, linkClass: string, children: React.ReactNode) => externalLinks
    ? <a href={to} target="_blank" rel="noopener noreferrer" className={linkClass}>{children}</a>
    : <AppLink to={to} className={linkClass}>{children}</AppLink>
  const titleNode = href
    ? link(href, 'rounded-sm text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]', title)
    : title

  return (
    <PlayerToneContext.Provider value="onColor">
      <div
        ref={ref}
        className={cn('min-w-0 overflow-hidden rounded-xl shadow-sm', locked && lockedPlacement === 'below' && 'border border-border', className)}
        style={paletteStyle(palette)}
        {...data}
      >
        <div
          className={cn('relative text-white', full ? 'p-4 sm:p-6' : 'p-3')}
          style={{ background: 'linear-gradient(135deg, rgb(var(--pl-hero)) 0%, rgb(var(--pl-hero-deep)) 100%)' }}
        >
          <div className={cn('flex min-w-0 items-center', full ? 'gap-4 sm:gap-6' : 'gap-3')}>
            {coverUrl ? (
              <img src={coverUrl} alt="" className={cn('aspect-square shrink-0 rounded-lg object-cover shadow-lg', full ? 'w-24 sm:w-40' : 'w-[4.5rem] sm:w-20')} />
            ) : (
              <span aria-hidden className={cn('flex aspect-square shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--pl-on-hero)/0.15)] shadow-lg', full ? 'w-24 sm:w-40' : 'w-[4.5rem] sm:w-20')}>
                <FallbackIcon className={full ? 'h-10 w-10' : 'h-7 w-7'} />
              </span>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {showTitle && (
                  showHref
                    ? link(showHref, cn('min-w-0 truncate rounded-sm font-medium text-white/90 hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]', full ? 'text-sm' : 'text-xs'), showTitle)
                    : <span className={cn('min-w-0 truncate font-medium text-white/90', full ? 'text-sm' : 'text-xs')}>{showTitle}</span>
                )}
                <span aria-hidden className="ml-auto shrink-0 text-white/90">{mark ?? <Podcast className="h-4 w-4" />}</span>
              </div>
              <p className={cn('mt-0.5 break-words font-bold leading-snug line-clamp-2', full ? 'text-xl sm:text-2xl' : 'text-[15px]')}>{titleNode}</p>
              {full && !locked && (
                <div className="mt-4 hidden items-center gap-3 sm:flex">
                  <ProgressBar position={position} duration={duration} chapters={chapters} onSeek={(s) => onSeek?.(s)} variant="thin" className="flex-1" />
                  <span className="whitespace-nowrap text-xs tabular-nums text-white/90">{timeText}</span>
                </div>
              )}
            </div>

            {locked ? (
              <span className={cn(ROUND, 'bg-[rgb(var(--pl-on-hero)/0.15)]', full ? 'h-14 w-14' : 'h-12 w-12')} role="img" aria-label={t('locked')}>
                <Lock className={full ? 'h-6 w-6' : 'h-5 w-5'} />
              </span>
            ) : (
              <button
                type="button"
                onClick={onPlay}
                disabled={disabled}
                aria-label={playLabel}
                title={playLabel}
                className={cn(ROUND, 'bg-[rgb(var(--pl-on-hero))] text-[rgb(var(--pl-hero-deep))] shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 motion-reduce:transition-none motion-reduce:hover:scale-100 focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--pl-hero))]', full ? 'h-14 w-14' : 'h-12 w-12')}
              >
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : playing ? <Pause className="h-6 w-6 fill-current" /> : <Play className="ml-0.5 h-6 w-6 fill-current" />}
              </button>
            )}
          </div>

          <div className={cn('flex items-center gap-1', full ? 'mt-3 sm:mt-2' : 'mt-2')}>
            {!locked && (
              <div className={cn('flex min-w-0 flex-1 items-center gap-2 pr-1', full && 'sm:hidden')}>
                <ProgressBar position={position} duration={duration} chapters={chapters} onSeek={(s) => onSeek?.(s)} variant="thin" className="flex-1" />
                <span className="whitespace-nowrap text-xs tabular-nums text-white/90">{timeText}</span>
              </div>
            )}
            {locked && lockedPlacement === 'inline' && <div className="min-w-0 flex-1">{locked}</div>}
            {locked && lockedPlacement === 'below' && <span className="flex-1 text-xs tabular-nums text-white/90">{duration ? formatClock(duration) : ''}</span>}
            {full && !locked && <span className="hidden flex-1 sm:block" />}
            {queue && (
              <button type="button" onClick={queue.onToggle} aria-pressed={queue.inQueue} aria-label={queue.inQueue ? t('queue.inQueue') : t('queue.add')} title={queue.inQueue ? t('queue.inQueue') : t('queue.add')}
                className={cn(ROUND, 'h-11 w-11 text-white/90 hover:bg-[rgb(var(--pl-on-hero)/0.15)] hover:text-white')}>
                {queue.inQueue ? <ListChecks className="h-5 w-5" /> : <ListPlus className="h-5 w-5" />}
              </button>
            )}
            {onShare && (
              <button type="button" onClick={onShare} aria-label={t('share')} title={t('share')} className={cn(ROUND, 'h-11 w-11 text-white/90 hover:bg-[rgb(var(--pl-on-hero)/0.15)] hover:text-white')}>
                <Share2 className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
        {locked && lockedPlacement === 'below' && <div className="bg-surface p-3 sm:p-4">{locked}</div>}
      </div>
    </PlayerToneContext.Provider>
  )
})
EpisodeEmbedCard.displayName = 'EpisodeEmbedCard'
